import { cn } from "@shared/utils/cn";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import {
    DEFAULT_RETAIL_DISTRIBUTION_DAYS,
    DEFAULT_RETAIL_MIN_BALANCE_USDV,
    DEFAULT_RETAIL_MIN_YIELD_PER_DISTRIBUTION_USDV
} from "@/config/retailYaasPolicy";
import { useYaasThresholds } from "@/hooks/yaas/useYaasThresholds";

interface AppFootnoteProps {
    /** Extra classes appended to the baseline Text styling — use for
     *  per-callsite positioning / sizing (mt-*, pb-*, max-w-*,
     *  responsive visibility). The baseline already covers
     *  `variant="fine-print"`, `leading-none`, `text-foreground-neutral`,
     *  `text-center`, and `text-balance`. */
    className?: string;
    /** Inline style — primarily for the USDv hero's collapse-on-input
     *  animation (max-height + padding-collapse driven by JS state). */
    style?: React.CSSProperties;
}

/**
 * The v3 protocol disclosure footnote — distribution cadence,
 * minimum balance, minimum accrued amount before distribution.
 * Appears at the bottom of any surface where YaaS distribution
 * mechanics are user-facing (USDv hero, USDv landing panel, retail
 * YaaS landing, etc.). Centralized so callers don't drift on copy
 * or threshold values; per-surface positioning/visibility flows in
 * through `className` / `style`.
 */
export function AppFootnote({
    className,
    style
}: AppFootnoteProps): React.ReactElement {
    const thresholds = useYaasThresholds();
    const minBalance = thresholds.minBalance ?? DEFAULT_RETAIL_MIN_BALANCE_USDV;
    const minYieldPerDistribution =
        thresholds.minYieldPerDistribution ??
        DEFAULT_RETAIL_MIN_YIELD_PER_DISTRIBUTION_USDV;
    return (
        <Text
            variant="fine-print"
            className={cn(
                "leading-none text-foreground-imprint text-center text-balance",
                className
            )}
            style={style}
        >
            <sup className="text-[8px]">1</sup> Rewards distribute every{" "}
            {thresholds.distributionDays ?? DEFAULT_RETAIL_DISTRIBUTION_DAYS}{" "}
            days. ${minBalance} minimum balance. ${minYieldPerDistribution}{" "}
            minimum amount accrued before distribution.
        </Text>
    );
}
